// src/utils/supportApi.js
const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_SIGNATURE_API ||
  process.env.REACT_APP_RELAYER_URL ||
  'http://localhost:3002';

const jsonHeaders = { 'Content-Type': 'application/json' };

// ── Public (user) endpoints ──

export async function createTicket({ userAddress, subject, description, category, priority, email, eventId, transactionHash }) {
  const res = await fetch(`${API_BASE}/api/support/tickets`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ userAddress, subject, description, category, priority, email, eventId, transactionHash }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create ticket');
  return data;
}

export async function fetchUserTickets(userAddress, { status, category, page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc' } = {}) {
  const params = new URLSearchParams({ userAddress, page: String(page), limit: String(limit), sortBy, sortOrder });
  if (status) params.set('status', status);
  if (category) params.set('category', category);
  const res = await fetch(`${API_BASE}/api/support/tickets?${params}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch tickets');
  return data.data;
}

export async function fetchTicketDetail(ticketId, userAddress) {
  const res = await fetch(`${API_BASE}/api/support/tickets/${ticketId}?userAddress=${userAddress}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch ticket');
  return data.data;
}

export async function addComment(ticketId, userAddress, comment) {
  const res = await fetch(`${API_BASE}/api/support/tickets/${ticketId}/comments`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ userAddress, comment }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to add comment');
  return data;
}

// ── Admin endpoints ──

function adminHeaders(adminSignature) {
  return { ...jsonHeaders, 'x-admin-signature': adminSignature || 'admin' };
}

export async function fetchAllTickets(adminSig, filters = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v) params.set(k, String(v));
  }
  const res = await fetch(`${API_BASE}/api/support/tickets?${params}`, {
    headers: adminHeaders(adminSig),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch tickets');
  return data.data;
}

export async function updateTicketStatus(adminSig, ticketId, status) {
  const res = await fetch(`${API_BASE}/api/support/tickets/${ticketId}/status`, {
    method: 'PATCH',
    headers: adminHeaders(adminSig),
    body: JSON.stringify({ status }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update status');
  return data;
}

export async function updateTicketPriority(adminSig, ticketId, priority) {
  const res = await fetch(`${API_BASE}/api/support/tickets/${ticketId}/priority`, {
    method: 'PATCH',
    headers: adminHeaders(adminSig),
    body: JSON.stringify({ priority }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update priority');
  return data;
}

export async function assignTicket(adminSig, ticketId, assignTo) {
  const res = await fetch(`${API_BASE}/api/support/tickets/${ticketId}/assign`, {
    method: 'PATCH',
    headers: adminHeaders(adminSig),
    body: JSON.stringify({ assignTo }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to assign ticket');
  return data;
}

export async function addAdminComment(adminSig, ticketId, comment) {
  const res = await fetch(`${API_BASE}/api/support/tickets/${ticketId}/comments`, {
    method: 'POST',
    headers: adminHeaders(adminSig),
    body: JSON.stringify({ comment }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to add comment');
  return data;
}

export async function fetchSupportStats(adminSig) {
  const res = await fetch(`${API_BASE}/api/support/stats`, {
    headers: adminHeaders(adminSig),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch stats');
  return data.data;
}
